import * as React from 'react';

import { useQuery } from '@apollo/client';

import { isAuthenticatedUser } from 'js/lib/user';

import { useEnterpriseLearningPathPageData } from 'bundles/enterprise-learning-path-description-page/contexts/EnterpriseLearningPathContext';
import EnterpriseLearningPathCurriculumContext from 'bundles/enterprise-learning-path-description-page/contexts/EnterpriseLearningPathCurriculumContext';
import { EnterpriseLearningPathCurriculumProductsQuery } from 'bundles/enterprise-learning-path-description-page/queries/EnterpriseLearningPathCurriculumProductsQuery';
import type {
  ProgramCurriculumProductsQuery,
  ProgramCurriculumProductsQueryVariables,
} from 'bundles/enterprise-learning-path-description-page/queries/__generated__/ProgramCurriculumProductsQuery';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';

type Props = {
  children: React.ReactNode;
};

// Fetches enrollment data for learning path curriculum.
const EnterpriseLearningPathCurriculumProvider = ({ children }: Props) => {
  const program = useProgram();
  const learningPathPageData = useEnterpriseLearningPathPageData();

  const queryIds = React.useMemo(() => {
    if (!program?.id || !learningPathPageData?.enterpriseBadge?.badgeActivities?.length) {
      return [];
    }

    return learningPathPageData.enterpriseBadge.badgeActivities.map(
      (course) => `${program.id}~VerifiedCertificate~${course.id}`
    );
  }, [program?.id, learningPathPageData?.enterpriseBadge?.badgeActivities]);

  const { data, loading, error } = useQuery<ProgramCurriculumProductsQuery, ProgramCurriculumProductsQueryVariables>(
    EnterpriseLearningPathCurriculumProductsQuery,
    {
      variables: {
        ids: queryIds,
      },
      skip: !queryIds.length || !isAuthenticatedUser(),
    }
  );

  const contextValue = React.useMemo(
    () => ({
      curriculumProducts: data?.ProgramCurriculumProductsV1Resource?.multiGet?.elements?.filter(Boolean) || [],
      loading,
      error,
    }),
    [data?.ProgramCurriculumProductsV1Resource?.multiGet?.elements, loading, error]
  );

  return (
    <EnterpriseLearningPathCurriculumContext.Provider value={contextValue}>
      {children}
    </EnterpriseLearningPathCurriculumContext.Provider>
  );
};

export default EnterpriseLearningPathCurriculumProvider;
